import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { showLegalLinks } from "./LegalPages";

const getAuthErrorMessage = (err, fallback) => {
  const payload = err?.response?.data;
  const detail = payload?.detail;

  if (typeof detail === "string" && detail.trim()) {
    return detail.trim();
  }

  if (detail && typeof detail === "object") {
    if (typeof detail.message === "string" && detail.message.trim()) {
      return detail.message.trim();
    }

    if (Array.isArray(detail.details) && detail.details.length > 0) {
      const first = detail.details[0];
      if (first && typeof first === "object") {
        const message = String(first.msg || "").trim();
        if (message) return message;
      }
    }
  }

  if (typeof payload?.message === "string" && payload.message.trim()) {
    return payload.message.trim();
  }

  const rawMessage = String(err?.message || "").toLowerCase();
  if (rawMessage.includes("network error") || rawMessage.includes("failed to fetch")) {
    return "Cannot reach backend API. Verify server is running and reachable.";
  }

  return fallback;
};

const Login = ({ onLogin }) => {
  const navigate = useNavigate();
  const [isSignup, setIsSignup] = useState(false);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [role, setRole] = useState("support_agent");
  const [selectedRoles, setSelectedRoles] = useState("support_agent");
  const roleTabs = [
    { label: "User", value: "user" },
    { label: "Support", value: "support_agent" },
    { label: "Admin", value: "admin" },
  ];
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [remember, setRemember] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState("");
  const [forgotSuccess, setForgotSuccess] = useState("");

  // Password change required state (admin first login)
  const [requirePasswordChange, setRequirePasswordChange] = useState(false);
  const [cognitoSession, setCognitoSession] = useState("");
  const [pendingUsername, setPendingUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  // Emailed sign-in code (Cognito EMAIL_OTP) state
  const [requireMfa, setRequireMfa] = useState(false);
  const [mfaSession, setMfaSession] = useState("");
  const [mfaUsername, setMfaUsername] = useState("");
  const [mfaDestination, setMfaDestination] = useState("");
  const [mfaRole, setMfaRole] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaLoading, setMfaLoading] = useState(false);

  // Signup email verification state
  const [requireEmailVerify, setRequireEmailVerify] = useState(false);
  const [verifyUsername, setVerifyUsername] = useState("");
  const [verifyDestination, setVerifyDestination] = useState("");
  const [verifyCode, setVerifyCode] = useState("");
  const [verifyInfo, setVerifyInfo] = useState("");
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const apiBase = process.env.REACT_APP_BACKEND_URL || "";

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const startEmailVerify = (verifyUser, destination) => {
    setVerifyUsername(verifyUser || "");
    setVerifyDestination(destination || "");
    setVerifyCode("");
    setVerifyInfo("");
    setError("");
    setSuccess("");
    setResendCooldown(60);
    setRequireEmailVerify(true);
  };

  const leaveEmailVerify = (successMessage = "") => {
    setRequireEmailVerify(false);
    setVerifyCode("");
    setVerifyInfo("");
    setError("");
    setIsSignup(false);
    setPassword("");
    setConfirmPassword("");
    setSuccess(successMessage);
  };

  const handleResendCode = async () => {
    const target = verifyUsername.trim();
    if (!target) return;
    setError("");
    setVerifyLoading(true);
    try {
      const res = await axios.post(`${apiBase}/api/auth/resend-verification`, { username: target });
      setVerifyInfo(res.data?.message || "We have sent a new code to your email.");
      setVerifyCode("");
      setResendCooldown(60);
    } catch (err) {
      setError(getAuthErrorMessage(err, "Could not send a new code. Please try again."));
      const retryAfter = Number(err?.response?.data?.retry_after);
      if (err?.response?.status === 429 && retryAfter > 0) {
        setVerifyInfo("");
        setResendCooldown(Math.ceil(retryAfter));
      }
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    setError("");
    const code = verifyCode.trim();
    if (!/^\d{6}$/.test(code)) {
      setError("Enter the 6-digit code from your email.");
      return;
    }
    setVerifyLoading(true);
    try {
      const res = await axios.post(`${apiBase}/api/auth/verify-email`, {
        username: verifyUsername.trim(),
        code,
      });
      leaveEmailVerify(res.data?.message || "Email verified. An administrator will review your request.");
    } catch (err) {
      setError(getAuthErrorMessage(err, "Verification failed. Please try again."));
      setVerifyCode("");
    } finally {
      setVerifyLoading(false);
    }
  };

  const startMfa = (data, fallbackUsername, requestedRole) => {
    setMfaSession(data.cognito_session || "");
    setMfaUsername(data.username || fallbackUsername);
    setMfaDestination(data.destination || "");
    setMfaRole(requestedRole);
    setMfaCode("");
    setError("");
    setRequirePasswordChange(false);
    setRequireMfa(true);
  };

  const backToSignIn = (message = "") => {
    setRequireMfa(false);
    setMfaSession("");
    setMfaCode("");
    setPassword("");
    setError(message);
  };

  const resetMessages = () => {
    setError("");
    setSuccess("");
  };

  const toggleMode = () => {
    setIsSignup((prev) => !prev);
    setPassword("");
    setConfirmPassword("");
    setShowPassword(false);
    setShowConfirmPassword(false);
    resetMessages();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    resetMessages();

    if (isSignup) {
      if (!selectedRoles) {
        setError("Please select an account type.");
        return;
      }
      if (!email.trim()) {
        setError("Email is required for signup.");
        return;
      }
      if (password.length < 8) {
        setError("Password must be at least 8 characters.");
        return;
      }
      if (!/[A-Z]/.test(password)) {
        setError("Password must contain at least 1 uppercase letter.");
        return;
      }
      if (!/[0-9]/.test(password)) {
        setError("Password must contain at least 1 number.");
        return;
      }
      if (!/[^A-Za-z0-9]/.test(password)) {
        setError("Password must contain at least 1 special character.");
        return;
      }
      if (password !== confirmPassword) {
        setError("Password and confirm password must match.");
        return;
      }
    }

    setLoading(true);
    try {
      if (isSignup) {
        const signupPayload = {
          username: username.trim(),
          email: email.trim().toLowerCase(),
          password,
          roles: [selectedRoles],
        };

        const res = await axios.post(`${apiBase}/api/auth/signup`, signupPayload);
        if (res?.data?.verification_required) {
          startEmailVerify(res.data.username || username.trim(), res.data.destination);
          return;
        }
        const token = res?.data?.token;
        const signupRole = res?.data?.user?.role || role;
        const signupUsername = res?.data?.user?.username || username;
        const signupRefreshToken = res?.data?.refresh_token || null;
        const signupExpiresAt = res?.data?.expires_at ?? null;

        if (!token && res?.data?.message) {
          setSuccess(res.data.message);
          setIsSignup(false);
        } else if (token) {
          onLogin(token, signupRole, signupUsername, signupRefreshToken, signupExpiresAt);
        } else {
          setSuccess("Signup successful. Please verify your email and sign in.");
          setIsSignup(false);
        }
      } else {
        const identity = username.trim();
        const loginPayload = { password, role };
        if (identity.includes("@")) {
          loginPayload.email = identity.toLowerCase();
        } else {
          loginPayload.username = identity;
        }
        const res = await axios.post(`${apiBase}/api/login`, loginPayload);
        if (res.data?.require_password_change) {
          setCognitoSession(res.data.cognito_session || "");
          setPendingUsername(res.data.username || username.trim());
          setRequirePasswordChange(true);
          return;
        }
        if (res.data?.require_mfa) {
          startMfa(res.data, identity, role);
          return;
        }
        onLogin(
          res.data.token,
          res.data.role,
          res.data.username,
          res.data.refresh_token || null,
          res.data.expires_at ?? null,
        );
      }
    } catch (err) {
      const fallback = isSignup
        ? "Signup failed. Please check your details."
        : "Login failed. Please check your credentials.";
      setError(getAuthErrorMessage(err, fallback));
    } finally {
      setLoading(false);
    }
  };
  const handleCompletePasswordChange = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (!/[A-Z]/.test(newPassword)) { setError("Password must contain at least 1 uppercase letter."); return; }
    if (!/[0-9]/.test(newPassword)) { setError("Password must contain at least 1 number."); return; }
    if (!/[^A-Za-z0-9]/.test(newPassword)) { setError("Password must contain at least 1 special character."); return; }
    if (newPassword !== confirmNewPassword) { setError("Passwords do not match."); return; }

    setChangePasswordLoading(true);
    try {
      const res = await axios.post(`${apiBase}/api/auth/complete-password-change`, {
        username: pendingUsername,
        cognito_session: cognitoSession,
        new_password: newPassword,
      });
      if (res.data?.require_mfa) {
        startMfa(res.data, pendingUsername, null);
        return;
      }
      onLogin(
        res.data.token,
        res.data.user?.role,
        res.data.user?.username,
        res.data.refresh_token || null,
        res.data.expires_at ?? null,
      );
    } catch (err) {
      const raw = getAuthErrorMessage(err, "");
      if (raw === "PASSWORD_CHANGE_SESSION_EXPIRED" || !raw) {
        setRequirePasswordChange(false);
        setError("Your session expired. Please log in again to set your password.");
      } else {
        // Show actual error in place — don't kick back to login screen
        setError(raw);
      }
    } finally {
      setChangePasswordLoading(false);
    }
  };

  const handleVerifyMfa = async (e) => {
    e.preventDefault();
    setError("");
    const code = mfaCode.trim();
    if (!/^\d{6}$/.test(code)) {
      setError("Enter the 6-digit code from your email.");
      return;
    }

    setMfaLoading(true);
    try {
      const body = { username: mfaUsername, cognito_session: mfaSession, code };
      if (mfaRole) body.role = mfaRole;
      const res = await axios.post(`${apiBase}/api/auth/verify-mfa`, body);
      onLogin(
        res.data.token,
        res.data.user?.role,
        res.data.user?.username,
        res.data.refresh_token || null,
        res.data.expires_at ?? null,
      );
    } catch (err) {
      const raw = getAuthErrorMessage(err, "");
      if (raw === "MFA_SESSION_EXPIRED" || !raw) {
        backToSignIn("Your verification code expired. Please sign in again to get a new code.");
      } else {
        setError(raw);
        setMfaCode("");
      }
    } finally {
      setMfaLoading(false);
    }
  };

  if (requireEmailVerify) {
    return (
      <div className="flex min-h-screen w-full bg-[#FAFAF6] overflow-hidden" style={{ height: "100vh" }}>
        <div className="flex flex-col justify-center w-full max-w-xl mx-auto px-12 py-10 h-full text-center">
          <div className="mb-8">
            <span className="inline-block font-bold text-lg text-cyan-500 mb-2">Arta</span>
            <h1 className="text-3xl font-extrabold mb-2 text-[#253439]">Verify your email</h1>
            <p className="text-gray-500">
              We sent a 6-digit code to{" "}
              <span className="font-semibold text-[#253439]">{verifyDestination || "your email address"}</span>.
              Enter it below to confirm your email. An administrator will then review your request.
            </p>
          </div>

          <form onSubmit={handleVerifyEmail} className="w-full">
            {error && <div className="mb-4 text-red-500 text-sm">{error}</div>}
            {verifyInfo && !error && <div className="mb-4 text-green-600 text-sm">{verifyInfo}</div>}
            <div className="mb-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Verification code</label>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 text-2xl tracking-[0.5em] text-center focus:outline-none focus:ring-2 focus:ring-cyan-400"
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ""))}
                required
                autoFocus
              />
            </div>
            <p className="text-xs text-gray-400 mb-6">
              The code expires in 15 minutes. Don't see it? Check your spam or junk folder.
            </p>
            <button
              type="submit"
              disabled={verifyLoading}
              className="w-full bg-cyan-500 hover:bg-cyan-600 text-white font-semibold py-3 rounded-lg transition disabled:opacity-50"
            >
              {verifyLoading ? "Verifying…" : "Verify email"}
            </button>
            <div className="mt-4 text-center text-sm text-gray-500">
              Didn't get a code?{" "}
              <button
                type="button"
                onClick={handleResendCode}
                disabled={verifyLoading || resendCooldown > 0}
                className="text-cyan-500 font-semibold hover:underline bg-transparent border-none p-0 m-0 cursor-pointer disabled:text-gray-400 disabled:no-underline disabled:cursor-not-allowed"
              >
                {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : "Resend code"}
              </button>
            </div>
          </form>

          <div className="mt-6 text-center text-sm text-gray-500">
            <button
              type="button"
              onClick={() => leaveEmailVerify()}
              className="text-cyan-500 font-semibold hover:underline bg-transparent border-none p-0 m-0 cursor-pointer"
            >
              Back to sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (requireMfa) {
    return (
      <div className="flex min-h-screen w-full bg-[#FAFAF6] overflow-hidden" style={{ height: "100vh" }}>
        <div className="flex flex-col justify-center w-full max-w-xl mx-auto px-12 py-10 h-full text-center">
          <div className="mb-8">
            <span className="inline-block font-bold text-lg text-cyan-500 mb-2">Arta</span>
            <h1 className="text-3xl font-extrabold mb-2 text-[#253439]">Check your email</h1>
            <p className="text-gray-500">
              We sent a 6-digit code to{" "}
              <span className="font-semibold text-[#253439]">{mfaDestination || "your email address"}</span>.
              Enter it below to finish signing in.
            </p>
          </div>
          <form onSubmit={handleVerifyMfa} className="w-full">
            {error && <div className="mb-4 text-red-500 text-sm">{error}</div>}
            <div className="mb-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Verification code</label>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 text-2xl tracking-[0.5em] text-center focus:outline-none focus:ring-2 focus:ring-cyan-400"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))}
                required
                autoFocus
              />
            </div>
            <p className="text-xs text-gray-400 mb-6">
              The code expires in a few minutes. Don't see it? Check your spam or junk folder.
            </p>
            <button
              type="submit"
              disabled={mfaLoading}
              className="w-full bg-cyan-500 hover:bg-cyan-600 text-white font-semibold py-3 rounded-lg transition disabled:opacity-50"
            >
              {mfaLoading ? "Verifying…" : "Verify & sign in"}
            </button>
          </form>
          <div className="mt-6 text-center text-sm text-gray-500">
            Didn't get a code?{" "}
            <button
              type="button"
              onClick={() => backToSignIn()}
              className="text-cyan-500 font-semibold hover:underline bg-transparent border-none p-0 m-0 cursor-pointer"
            >
              Back to sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (requirePasswordChange) {
    return (
      <div className="flex min-h-screen w-full bg-[#FAFAF6] overflow-hidden" style={{ height: "100vh" }}>
        <div className="flex flex-col justify-center w-full max-w-xl px-12 py-10 h-full">
          <div className="mb-8">
            <span className="inline-block font-bold text-lg text-cyan-500 mb-2">Arta</span>
            <h1 className="text-3xl font-extrabold mb-2 text-[#253439]">Set a new password</h1>
            <p className="text-gray-500">Your account requires a password change before you can continue.</p>
          </div>
          <form onSubmit={handleCompletePasswordChange} className="w-full">
            {error && <div className="mb-4 text-red-500 text-sm">{error}</div>}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">New password</label>
              <div className="relative">
                <input
                  type={showNewPassword ? "text" : "password"}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 pr-16 focus:outline-none focus:ring-2 focus:ring-cyan-400"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-cyan-600 font-semibold hover:text-cyan-800 focus:outline-none"
                  tabIndex={-1}
                >
                  {showNewPassword ? "Hide" : "Show"}
                </button>
              </div>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm new password</label>
              <div className="relative">
                <input
                  type={showConfirmNewPassword ? "text" : "password"}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 pr-16 focus:outline-none focus:ring-2 focus:ring-cyan-400"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmNewPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-cyan-600 font-semibold hover:text-cyan-800 focus:outline-none"
                  tabIndex={-1}
                >
                  {showConfirmNewPassword ? "Hide" : "Show"}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={changePasswordLoading}
              className="w-full bg-cyan-500 hover:bg-cyan-600 text-white font-semibold py-2 rounded-lg transition disabled:opacity-50"
            >
              {changePasswordLoading ? "Saving…" : "Set password & continue"}
            </button>
          </form>
        </div>
      </div>
    );
  }
  return (
    <div className="flex min-h-screen w-full bg-[#FAFAF6] overflow-hidden" style={{ height: "100vh" }}>
      <div className="flex flex-col justify-center w-full max-w-xl px-12 py-10 h-full">
        <div className="mb-8">
          <span className="inline-block font-bold text-lg text-cyan-500 mb-2">Arta</span>
          <h1 className="text-3xl font-extrabold mb-2 text-[#253439]">
            {isSignup ? "Create your account" : "Hello! Welcome Back"}
          </h1>
          <p className="text-gray-500">
            {isSignup ? "Sign up to access Arta support." : "Welcome to Arta support!"}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="w-full">
          {/*
            Old version (kept for reference):
            <div className="flex mb-6">
              <button
                type="button"
                className={`flex-1 py-2 rounded-l-lg font-semibold border ${
                  role === "user" ? "bg-cyan-500 text-white" : "bg-gray-100 text-gray-700"
                }`}
                onClick={() => setRole("user")}
              >
                User
              </button>
              <button
                type="button"
                className={`flex-1 py-2 rounded-r-lg font-semibold border-l-0 border ${
                  role === "agent" ? "bg-cyan-500 text-white" : "bg-gray-100 text-gray-700"
                }`}
                onClick={() => setRole("agent")}
              >
                Agent
              </button>
            </div>
          */}
          {isSignup ? (
            <div className="mb-5 relative">
              <label className="block text-sm text-gray-500 mb-1">I am signing up as</label>
              <select
                className="w-full border rounded px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-[#FAFAF6] text-[#253439]"
                value={selectedRoles}
                onChange={(e) => setSelectedRoles(e.target.value)}
              >
                <option value="support_agent">Support Agent</option>
                <option value="user">Enterprise User</option>
              </select>
            </div>
          ) : (
            <div className="mb-6 relative flex bg-gray-100 rounded-lg border border-gray-200 p-1">
              <div
                className="absolute top-1 bottom-1 rounded-md bg-cyan-500"
                style={{
                  width: "calc((100% - 8px) / 3)",
                  left: "4px",
                  transform: `translateX(calc(${roleTabs.findIndex((t) => t.value === role)} * 100%))`,
                  transition: "transform 0.25s ease-in-out",
                }}
              />
              {roleTabs.map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  className={`relative flex-1 py-2.5 font-semibold text-sm z-10 rounded-md tracking-wide ${
                    role === tab.value ? "text-white" : "text-gray-500 hover:text-gray-700"
                  }`}
                  onClick={() => setRole(tab.value)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          {isSignup && (
            <div className="mb-4">
              <input
                type="email"
                className="w-full border rounded px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-[#FAFAF6]"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
          )}

          <div className="mb-4">
            <input
              type="text"
              className="w-full border rounded px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-[#FAFAF6]"
              placeholder={isSignup ? "Username" : "Username or Email"}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoFocus
            />
          </div>

          <div className="mb-1 relative">
            <input
              type={showPassword ? "text" : "password"}
              className="w-full border rounded px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-[#FAFAF6] pr-12"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 bg-transparent border-none p-0 cursor-pointer text-sm"
              tabIndex={-1}
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>

          {isSignup && (
            <p className="text-xs text-gray-400 mb-3 ml-1">
              Min. 8 characters &bull; 1 uppercase &bull; 1 number &bull; 1 special character
            </p>
          )}

          {isSignup && (
            <div className="mb-4 relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                className="w-full border rounded px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-[#FAFAF6] pr-12"
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 bg-transparent border-none p-0 cursor-pointer text-sm"
                tabIndex={-1}
                onClick={() => setShowConfirmPassword((v) => !v)}
                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
              >
                {showConfirmPassword ? "Hide" : "Show"}
              </button>
            </div>
          )}

          {!isSignup && (
            <div className="flex items-center justify-between mb-6">
              <label className="flex items-center text-sm">
                <input
                  type="checkbox"
                  className="mr-2 accent-cyan-500"
                  checked={remember}
                  onChange={() => setRemember((v) => !v)}
                />
                Remember me
              </label>
              <button
                type="button"
                className="text-cyan-500 text-sm hover:underline bg-transparent border-none p-0 m-0 cursor-pointer"
                tabIndex={0}
                onClick={() => { setForgotError(""); setForgotSuccess(""); setForgotEmail(""); setShowForgotModal(true); }}
              >
                Forgot Username / Password?
              </button>
            </div>
          )}

          {error && <div className="mb-4 text-red-600 text-sm">{error}</div>}
          {success && <div className="mb-4 text-green-600 text-sm">{success}</div>}

          <button
            type="submit"
            className="w-full bg-cyan-500 hover:bg-cyan-600 text-white font-semibold py-3 rounded-lg text-base transition"
            disabled={loading}
          >
            {loading
              ? isSignup
                ? "Creating account..."
                : "Signing in..."
              : isSignup
                ? "Sign Up"
                : "Sign In"}
          </button>
        </form>

        <div className="mt-8 text-center text-sm text-gray-500">
          {isSignup ? "Already have an account? " : "Don't have an account? "}
          <button
            type="button"
            className="text-cyan-500 font-semibold hover:underline bg-transparent border-none p-0 m-0 cursor-pointer"
            tabIndex={0}
            onClick={toggleMode}
          >
            {isSignup ? "Sign In" : "Sign Up"}
          </button>
        </div>

        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={() => navigate("/about")}
            className="text-sm text-gray-400 hover:text-cyan-500 transition bg-transparent border-none p-0 m-0 cursor-pointer underline underline-offset-2"
          >
            About Us
          </button>
          {showLegalLinks && (
            <>
              <span className="text-sm text-gray-300 mx-2">·</span>
              <button
                type="button"
                onClick={() => navigate("/privacy")}
                className="text-sm text-gray-400 hover:text-cyan-500 transition bg-transparent border-none p-0 m-0 cursor-pointer underline underline-offset-2"
              >
                Privacy Policy
              </button>
              <span className="text-sm text-gray-300 mx-2">·</span>
              <button
                type="button"
                onClick={() => navigate("/terms")}
                className="text-sm text-gray-400 hover:text-cyan-500 transition bg-transparent border-none p-0 m-0 cursor-pointer underline underline-offset-2"
              >
                Terms of Service
              </button>
            </>
          )}
        </div>
      </div>

      <div className="hidden md:flex flex-1 items-center justify-center bg-[#FAFAF6] h-full">
        <img
          src="/Arta_login_page.png"
          alt="Arta Login Illustration"
          className="w-full h-full object-cover rounded-l-3xl"
          style={{ maxHeight: "100vh" }}
        />
      </div>

      {/* Forgot Username / Password modal */}
      {showForgotModal && (
        <div className="fixed inset-0 z-[300] bg-black/40 flex items-center justify-center px-4">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-200">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold text-[#253439]">Forgot Username / Password?</h3>
              <button
                type="button"
                onClick={() => setShowForgotModal(false)}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none bg-transparent border-none cursor-pointer"
              >
                ✕
              </button>
            </div>

            <div className="p-5">
              {!forgotSuccess ? (
                <>
                  <p className="text-sm text-gray-600 mb-4">
                    Enter the email address associated with your account. We'll send you your username and a temporary password.
                  </p>
                  <input
                    type="email"
                    className="w-full border rounded px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-[#FAFAF6] mb-4"
                    placeholder="Email address"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    autoFocus
                  />
                  {forgotError && (
                    <div className="mb-3 text-sm text-red-600">{forgotError}</div>
                  )}
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setShowForgotModal(false)}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={forgotLoading}
                      onClick={async () => {
                        if (!forgotEmail.trim() || !forgotEmail.includes("@")) {
                          setForgotError("Please enter a valid email address.");
                          return;
                        }
                        setForgotLoading(true);
                        setForgotError("");
                        try {
                          const res = await axios.post(
                            `${apiBase}/api/auth/forgot-password`,
                            { email: forgotEmail.trim().toLowerCase() }
                          );
                          const msg = res.data?.message;
                          setForgotSuccess(typeof msg === "string" ? msg : "Check your email.");
                        } catch (err) {
                          const detail = err.response?.data?.detail;
                          let errMsg = "Something went wrong. Please try again.";
                          if (typeof detail === "string") errMsg = detail;
                          else if (Array.isArray(detail) && detail[0]?.msg) errMsg = detail[0].msg;
                          else if (detail && typeof detail === "object" && detail.message) errMsg = String(detail.message);
                          setForgotError(errMsg);
                        } finally {
                          setForgotLoading(false);
                        }
                      }}
                      className="px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white text-sm font-semibold disabled:opacity-60"
                    >
                      {forgotLoading ? "Sending…" : "Send Reset Email"}
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-center py-4">
                  <div className="text-green-600 font-semibold mb-2">Email sent!</div>
                  <p className="text-sm text-gray-600 mb-4">{forgotSuccess}</p>
                  <button
                    type="button"
                    onClick={() => setShowForgotModal(false)}
                    className="px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white text-sm font-semibold"
                  >
                    Back to Sign In
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
